import type {
  BulkAction,
  BulkIssue,
  BulkPreviewProduct,
  BulkResult,
  InventoryFamily,
} from "../api/types";

/**
 * What the backend's vocabulary is called on screen.
 *
 * Labels only. Nothing here decides what a workbook would do — that answer is
 * the server's, arrives whole, and is rendered. A second opinion in the browser
 * about whether a row creates or updates would be a second definition of the
 * import contract.
 */

export const ACTION_LABELS: Record<BulkAction, string> = {
  created: "Create",
  updated: "Update",
  unchanged: "No change",
};

export const RESULT_LABELS: Record<BulkResult, string> = {
  created: "Created",
  updated: "Updated",
  unchanged: "No change",
  failed: "Failed",
};

export const FAMILY_LABELS: Record<InventoryFamily, string> = {
  glasses: "Glasses",
  contact_lenses: "Contact lenses",
  accessories: "Accessories",
};

export function familyLabel(family: InventoryFamily | null): string {
  return family ? FAMILY_LABELS[family] : "Unclassified";
}

/** Where an issue is, in the words a spreadsheet uses. */
export function issueLocation(issue: BulkIssue): string {
  const parts: string[] = [];
  if (issue.row !== null) parts.push(`Row ${issue.row}`);
  if (issue.column) parts.push(issue.column);
  return parts.join(" · ");
}

/**
 * What this product would do to stock, in one sentence.
 *
 * Said explicitly for the same reason the page says it once at the top: an
 * operator about to rewrite a catalogue is entitled to know, per product,
 * whether anything is about to be opened — and to be told plainly that nothing
 * already on a shelf is being touched.
 */
export function stockSentence(item: BulkPreviewProduct): string {
  if (item.creates_variants === 0) {
    return "No new stock records.";
  }
  const variants = item.creates_variants === 1
    ? "1 new sellable variant"
    : `${item.creates_variants} new sellable variants`;
  if (item.opening_stock === 0) {
    return `${variants}, opening at zero.`;
  }
  return `${variants}, opening at ${item.opening_stock} in total.`;
}

/** The file a browser will not otherwise offer to save. */
export function saveFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
